import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import produce from 'immer';
import {marked} from 'marked';
import React, {useState} from 'react';

import {FormDefinitionField} from 'forms/form-designer/Constants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {ColumnConstraint} from 'notebook/tables/TableConstants';
import TypedValueEditor from 'notebook/tables/typed-values/components/editors/TypedValueEditor';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';

export interface FormInstanceTypedValueFieldProps {
  fieldDefinition: FormDefinitionField;
  columnConstraint: ColumnConstraint;
  evaluatedCell?: EvaluatedCell;
  updateTypedValue: (typedValue: TypedValue) => void;
}

export function FormInstanceTypedValueField({
  fieldDefinition,
  columnConstraint,
  evaluatedCell,
  updateTypedValue,
}: FormInstanceTypedValueFieldProps): JSX.Element {
  const {key, label, isRequired, description} = fieldDefinition;
  // Override isRequired with Form Definition value
  const modifiedColumnConstraint = produce(columnConstraint, (draftColumnConstraint) => {
    draftColumnConstraint.structuredTableColumn!.isRequired = isRequired;
    draftColumnConstraint.columnDefinition!.isRequired = isRequired;
  });
  const columnDefinition = modifiedColumnConstraint.columnDefinition!;

  const typedValue = evaluatedCell?.value ?? {type: BaseTypedValueType.STRING, rawValue: ''};
  const rowMetadata = null;
  const [query, setQuery] = useState<string>();
  return (
    <InputWrapper
      className={`form-group ${css(styles.inputBox)}`}
      id={key}
      label={label}
      isRequired={isRequired}
      labelStyle={FieldLabel.STYLES.LABEL}
      help={
        description && (
          <span
            className="formInstance-formFieldDescription"
            // oxlint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(marked(description))}}
          />
        )
      }
      helpInteraction={FloatingInteraction.HOVER}
    >
      <TypedValueEditor
        typedValue={typedValue}
        evaluatedCell={evaluatedCell}
        locked={columnDefinition.locked || evaluatedCell?.locked}
        query={query}
        isRequired={isRequired}
        onUpdateTypedValue={updateTypedValue}
        onUpdateQuery={setQuery}
        columnConstraint={modifiedColumnConstraint}
        rowMetadata={rowMetadata}
        initialTypedValue={typedValue}
      />
    </InputWrapper>
  );
}

const styles = StyleSheet.create({
  inputBox: {
    marginBottom: 0,
    padding: '0 8px',
  },
});
